import React from "react";
import { Elements, RichText } from "prismic-reactjs";
import PrismicJS from "prismic-javascript";
import { getArticleAsUrl, getUrlWithSearchParams } from "src/services/UrlService";
import YouTubeEmbed, { getYouTubeVideoId } from "src/components/YouTubeEmbed";

// Use imgix api that comes with Prismic to force resize
export const imgixResize = (imageUrl: string, { width = 800, height }: { width?: number; height?: number } = {}) => {
  return getUrlWithSearchParams(imageUrl, {
    rect: undefined,
    fit: "crop",
    w: width,
    h: height,
  });
};

export const getMasterAPI = async (options = {}) =>
  await PrismicJS.api("https://betql-staging.prismic.io/api/v2", options); // eslint-disable-line

export const parseTags = (tags: string) => {
  const rawTagNames = !tags
    ? []
    : tags
        .trim() // no whitespace at start and end
        .replace(/#/g, "") // hash becomes space
        .replace(/,/g, "") // comma becomes space
        .replace(/\s+/g, " ") // remove multiple spaces
        .split(" "); // separate the tag names

  return rawTagNames.map((raw: string) => {
    // insert space before each word
    const pretty = raw.replace(/([A-Z][a-z0-9]+)/g, " $1").trim();
    return { raw, pretty };
  });
};

export const articleAsText = (doc: any) => {
  const textArr = doc.body?.map((slice: any) => {
    const isText = slice?.type === "text" && slice?.primary?.text;
    return isText ? RichText.asText(slice?.primary?.text) : slice?.primary?.text;
  }) || ["No preview available."];
  return textArr.join(" ");
};

export const renderRichText = (value: any) => RichText.render(value, getArticleAsUrl as any, null);

export const renderArticleRichText = (value: any) =>
  RichText.render(value, getArticleAsUrl as any, articleHtmlSerializer);

export const hasHeroContent = (value: any): value is any[] => Array.isArray(value) && value.length > 0;

export const renderHeroRichText = (value: any) => RichText.render(value, getArticleAsUrl as any, heroHtmlSerializer);

const propsWithUniqueKey = function (props: any, key: any) {
  return Object.assign(props || {}, { key });
};

// List of domains for we do not apply rel="nofollow".
const followDomains = ["realgm.com"];

const articleHtmlSerializer = function (type: any, element: any, _: any, children: any, key: any) {
  let props = {};
  switch (type) {
    case Elements.paragraph: // Paragraph
      props = {
        className: "rotoql-content-article__paragraph",
      };
      return React.createElement("p", propsWithUniqueKey(props, key), children);
    case Elements.hyperlink: // Link
      // Default link handling
      const target = (element.data.target !== "_self" && element.data.target) || undefined;

      const url = new URL(element.data.url);
      let href = url.href;
      if (url?.host === "betql.co") {
        href = url.href.replace("https://betql.co", "");
      }

      const isExternal = !href.startsWith("/");
      const noFollow = isExternal && followDomains.every((domain) => !href.includes(domain));
      const rel = [isExternal && "noopener", noFollow && "nofollow"].filter(Boolean).join(" ") || undefined;
      return React.createElement("a", propsWithUniqueKey({ href, rel, target }, key), children);
    case Elements.embed: // Embed
      const youTubeVideoId = getYouTubeVideoId(element.oembed.embed_url);
      if (youTubeVideoId) {
        return React.createElement(
          "div",
          propsWithUniqueKey({ className: "rotoql-content-article__youtube" }, key),
          React.createElement(YouTubeEmbed, { videoId: youTubeVideoId, title: element.oembed.title })
        );
      }
      props = Object.assign(
        {
          className: "rotoql-content-article__video",
          "data-oembed": element.oembed.embed_url,
          "data-oembed-type": element.oembed.type,
          "data-oembed-provider": element.oembed.provider_name,
        },
        element.label ? { className: element.label } : {}
      );
      const embedHtml = React.createElement(
        "div",
        Object.assign({ style: { display: "flex" } }, { dangerouslySetInnerHTML: { __html: element.oembed.html } })
      );
      return React.createElement("div", propsWithUniqueKey(props, key), embedHtml);
    default:
      return null;
  }
};

const heroHtmlSerializer = function (type: any, element: any, _: any, children: any, key: any) {
  const headingProps = (level: number) => ({
    className: `rotoql-seo-hero__heading rotoql-seo-hero__heading--h${level}`,
  });

  switch (type) {
    case "heading1":
      return React.createElement("h1", propsWithUniqueKey(headingProps(1), key), children);
    case "heading2":
      return React.createElement("h2", propsWithUniqueKey(headingProps(2), key), children);
    case "heading3":
      return React.createElement("h3", propsWithUniqueKey(headingProps(3), key), children);
    case "heading4":
      return React.createElement("h4", propsWithUniqueKey(headingProps(4), key), children);
    case "heading5":
      return React.createElement("h5", propsWithUniqueKey(headingProps(5), key), children);
    case "heading6":
      return React.createElement("h6", propsWithUniqueKey(headingProps(6), key), children);
    case Elements.paragraph:
      return React.createElement("p", propsWithUniqueKey({ className: "rotoql-seo-hero__paragraph" }, key), children);
    case "group-list-item":
      return React.createElement("ul", propsWithUniqueKey({ className: "rotoql-seo-hero__list" }, key), children);
    case "group-o-list-item":
      return React.createElement("ol", propsWithUniqueKey({ className: "rotoql-seo-hero__list" }, key), children);
    case "list-item":
    case "o-list-item":
      return React.createElement("li", propsWithUniqueKey({}, key), children);
    case "preformatted":
      return React.createElement(
        "pre",
        propsWithUniqueKey({ className: "rotoql-seo-hero__preformatted" }, key),
        children
      );
    case Elements.hyperlink:
      return React.createElement(
        "a",
        propsWithUniqueKey(
          {
            href: element.data.url,
            target: element.data.target || undefined,
            className: "rotoql-seo-hero__link",
          },
          key
        ),
        children
      );
    case Elements.embed:
      return React.createElement(
        "div",
        propsWithUniqueKey({ className: "rotoql-seo-hero__embed" }, key),
        React.createElement("div", { dangerouslySetInnerHTML: { __html: element.oembed.html } })
      );
    default:
      return null;
  }
};
